import { Fragment } from 'react';
import './PrintPlan.css';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { ltfiPerYear, CE_TIERS, tierForRiskScore } from './calcEngine';

function num(v) {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function fmtD(v) {
  if (v == null) return '—';
  return '$' + Math.round(v).toLocaleString();
}

function fmtDM(v) {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  return '$' + Math.round(v).toLocaleString();
}


function fmtChartY(v) {
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000) return '$' + Math.round(v / 1_000) + 'K';
  return '$' + v;
}

// Fallback-only: for saved tables predating the engine-computed `legacy` field.
function calcAfterTaxLegacy(row) {
  const ira = (row.iraBalance || 0) * 0.75;
  const roth = (row.rothBalance || 0) * 1.0;
  const nq = (row.nonQualBalance || 0) * 0.95;
  const annuity = (row.annuityBalance || 0) * 0.75;
  return Math.round(ira + roth + nq + annuity);
}

function printDate() {
  return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// Running-header document title — set once per render by PrintPlan (LTFI edition swaps
// it to the Paycheck title so pages 2-8 match the cover). Render-synchronous, so a
// module-scoped value is safe here.
let PP_DOC_TITLE = 'Your Custom Convert Engine Plan';

function PageHeader({ clientName }) {
  return (
    <div className="pp-hdr">
      <div className="pp-hdr-left">
        <div className="pp-hdr-logo">JBF</div>
        <span className="pp-hdr-brand">J Bradin Financial</span>
      </div>
      <div className="pp-hdr-right">
        <span className="pp-hdr-client">{clientName}</span>
        <span className="pp-hdr-doc">{PP_DOC_TITLE}</span>
      </div>
    </div>
  );
}

function PageFooter({ pageNum, total }) {
  return (
    <div className="pp-ftr">
      <span>J Bradin Financial — Confidential</span>
      <span>Page {pageNum} of {total}</span>
    </div>
  );
}

function Page({ num: pageNum, total, clientName, cover, autoHeight, children }) {
  if (cover) return <div className="pp-page pp-cover-page">{children}</div>;
  return (
    <div className={`pp-page${autoHeight ? ' pp-page-auto' : ''}`}>
      <PageHeader clientName={clientName} />
      <div className={`pp-body${autoHeight ? ' pp-body-auto' : ''}`}>{children}</div>
      <PageFooter pageNum={pageNum} total={total} />
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="pp-section-title">{children}</div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// The 8-page ONE-STORY report. Every page is one beat of the same story the ad
// and the LP tell: your IRA owes a debt → a small Roth engine pays it → you
// make one decision → then it runs without you.
//   1 Cover (DEBT → ENGINE → $0 strip)
//   2 Your IRA's Tax Debt
//   3 The Engine That Pays It
//   4 Year One — Approve the Plan & Start the Engine
//   5 The Engine Pays. You Don't. (autopilot years)
//   6 Debt Hits $0 — What You Keep
//   7 Why Jesse
//   8 Disclosures
// Full planning depth (multi-scenario comparison, bracket detail) stays
// backstage in the on-screen app and the Excel export — never in this artifact.
// ─────────────────────────────────────────────────────────────────────────────
export function PrintPlan({ form, results, conversionTarget, printScenarioIds = [], liveScenarioId = null }) {
  if (!results || results.error) return null;

  const clientName = [form.clientFirstName, form.clientLastName].filter(Boolean).join(' ') || 'Client';
  const spouseName = form.includeSpouse
    ? [form.spouseFirstName, form.spouseLastName].filter(Boolean).join(' ')
    : null;
  const fullNames = spouseName ? `${clientName} & ${spouseName}` : clientName;

  // ── LIVE-scenario narrative anchor ───────────────────────────────────────────
  // The scenario the advisor flagged LIVE drives the whole story. When no scenario
  // is LIVE (older plans), fall back to the first JBF Convert Engine scenario.
  const liveScen = liveScenarioId != null
    ? form.rothScenarios.find(s => s.id === liveScenarioId)
    : null;
  // Only a LIVE *JBF* scenario re-anchors the story; a LIVE non-JBF scenario
  // leaves the narrative on the first JBF scenario, so the report never degrades.
  const narrativeScen = (liveScen && liveScen.vehicle === 'jbf' ? liveScen : null)
    || form.rothScenarios.find(s => s.vehicle === 'jbf')
    || null;
  const narrativeId = String(narrativeScen?.id ?? '');

  // YOUR GOALS is client-goals text (vehicle-agnostic): prefer the LIVE scenario's text
  // when it has any, else keep the prior fallback so the callout never goes blank.
  const goalsScenario = (liveScen && (liveScen.clientGoals || '').trim() ? liveScen : null)
    || form.rothScenarios.find(s => s.vehicle === 'jbf' && (s.clientGoals || '').trim())
    || form.rothScenarios.find(s => (s.clientGoals || '').trim());
  const clientGoalsText = (goalsScenario?.clientGoals || '').trim();

  const { totalAssets } = results;
  const lifeExpectancy = num(form.lifeExpectancy) || 90;

  // Goal lens — matches the on-screen dashboard toggle (Legacy vs Tax-Free Lifetime Income).
  const goalLean = form.goalLean === 'ltfi' ? 'ltfi' : 'legacy';
  const ppJbfScen = narrativeScen;
  const ppJbfId = narrativeId;
  const ltfiIncome = (() => {
    const ext = results.v2JBFExtras?.[ppJbfId];
    if (!ext) return 0;
    const t = results.v2ScenarioTables?.[ppJbfId];
    const roth = ext.conversionCompleteRow?.rothBalance
      ?? (t ? [...t].reverse().find(r => r.conversionAmount > 0)?.rothBalance : 0) ?? 0;
    return Math.round(roth * ((parseFloat(ppJbfScen?.ltfiYield) || 6) / 100));
  })();

  const validScenarioIds = form.rothScenarios
    .filter(s => results.v2ScenarioTables?.[s.id])
    .map(s => s.id);
  const printScens = printScenarioIds.filter(id => validScenarioIds.includes(id));
  const hasComparisonPages = validScenarioIds.length > 0 && results.v2Summaries && results.v2BaselineTable;

  // ── One-story numbers: DEBT → ENGINE → $0 ──────────────────────────────────
  // Debt = the IRS's modeled claim on the IRA: the engine-computed heirs'
  // inherited-IRA tax (row.iraDeferredTax), the same basis Legacy uses everywhere
  // else in this document — so the debt number can never diverge from the plan.
  const storyBRows = results.v2BaselineTable || [];
  const debtToday = storyBRows[0]?.iraDeferredTax ?? null;
  const storyRmdAge = storyBRows.find(r => (r.rmd || 0) > 0)?.age ?? null;
  const debtAtRmd = storyRmdAge != null
    ? (storyBRows.find(r => r.age === storyRmdAge)?.iraDeferredTax ?? null)
    : (storyBRows.length ? Math.max(...storyBRows.map(r => r.iraDeferredTax || 0)) : null);
  const firstBaselineRMD = storyRmdAge != null ? (storyBRows.find(r => r.age === storyRmdAge)?.rmd ?? null) : null;
  const storyTable = narrativeId ? (results.v2ScenarioTables?.[narrativeId] || []) : [];
  const storyExtras = narrativeId ? results.v2JBFExtras?.[narrativeId] : null;
  const storyConvRows = storyTable.filter(r => num(r.conversionAmount) > 0);
  const storyEngineSize = (() => {
    const t = num(narrativeScen?.ceTargetSize);
    if (t > 0) return t;
    return num(storyConvRows[0]?.conversionAmount) || num(narrativeScen?.year1Amount) || num(narrativeScen?.conversionAmount) || 0;
  })();
  const storyCostToStart = storyExtras?.year1ExternalTax ?? 0;
  const storyExtList = storyConvRows
    .map((r, i) => ({ yr: i + 1, amt: Math.round(r.externalTaxPaid || 0) }))
    .filter(x => x.amt > 0);
  const storyZeroFromYr = storyExtList.length ? storyExtList[storyExtList.length - 1].yr + 1 : 1;
  // Total conversion tax across the plan (engine-paid + client-paid) — used by the
  // Page-3 opener to make "checks that size" case-true. 0 on older saved tables
  // that predate conversionTaxCost; copy degrades gracefully.
  const storyTotalConvTax = storyConvRows.reduce((s, r) => s + (r.conversionTaxCost || 0), 0);
  // ── TFLI sleeve story numbers (all 0 / null when the live scenario has no TFLI bucket,
  //    so every gate below collapses and non-TFLI plans print byte-identical) ──────────
  const storyHasTfli     = storyTable.some(r => (r.tfliIncome || 0) > 0);
  const storyTfliYear1   = Math.round(storyTable[0]?.tfliIncome || 0);
  const storyTfliSteady  = storyConvRows.length ? Math.round(Math.max(...storyConvRows.map(r => r.tfliIncome || 0))) : 0;
  const storyTfliPlateauAge = storyTfliSteady > 0
    ? (storyTable.find(r => (r.tfliIncome || 0) >= storyTfliSteady - 1)?.age ?? null) : null;
  const storyTfliLastConvAge = storyConvRows.length ? storyConvRows[storyConvRows.length - 1].age : null;
  const storyTfliBucket  = Math.round(storyTable.reduce((m, r) => Math.max(m, r.tfliBucketBalance || 0), 0));
  const storyTfliPaid    = Math.round(storyTable.reduce((s, r) => s + (r.tfliIncome || 0), 0));
  const storyTfliAllocY1 = Math.round(storyTable[0]?.tfliAlloc || 0);
  const storyTfliAllocY2 = Math.round(storyTable[1]?.tfliAlloc || 0);
  const storyTfliPaidY12 = Math.round((storyTable[0]?.tfliIncome || 0) + (storyTable[1]?.tfliIncome || 0));
  // ── LTFI EDITION (Aug 2026): the goal lens picks the STORY, not just numbers.
  //    TFLI lens + a live TFLI sleeve renders the "Paycheck report"; the Legacy lens
  //    (or a plan without a sleeve) keeps the debt-elimination report byte-identical.
  //    Compliance: every lifetime claim carries † → LIFE_FN; copy is modeled/projected,
  //    never guaranteed. ─────────────────────────────────────────────────────────────
  const ltfiEdition = goalLean === 'ltfi' && storyHasTfli;
  PP_DOC_TITLE = ltfiEdition ? 'Your Tax-Free Paycheck Plan' : 'Your Custom Convert Engine Plan';
  const LIFE_FN = `† "For life" figures are projections to age ${lifeExpectancy} using this plan's stated planning assumptions. Distributions and income are modeled, not guaranteed, and will vary with portfolio performance, tax law, and your decisions.`;
  const LifeFootnote = () => (
    <div className="pp-yby-footnote" style={{ marginTop: '8px' }}>{LIFE_FN}</div>
  );
  const storyDebtPaidAge = storyExtras?.conversionCompleteRow?.age
    ?? (storyConvRows.length ? storyConvRows[storyConvRows.length - 1].age : null);
  // Honesty gate: "debt hits $0" is only claimed when the plan actually empties the
  // IRA. Both residuals come from the engine's own end-of-plan row — same basis as
  // Legacy — so a partial-conversion schedule automatically gets the truthful
  // "debt falls" framing instead.
  const storyResidualIra = Math.round(storyTable.at(-1)?.iraBalance || 0);
  const storyResidualDebt = Math.round(storyTable.at(-1)?.iraDeferredTax || 0);
  const storyFullConversion = storyTable.length > 0 && storyResidualIra < 1000;
  // Partial-conversion comparison must be scenario-vs-baseline at the SAME end age —
  // a partially-converted IRA can still grow, so "vs today" would understate (or
  // zero out) the plan's real debt reduction.
  const storyBaselineResidualDebt = Math.round(storyBRows.at(-1)?.iraDeferredTax || 0);
  const storyDebtCut = Math.max(0, storyBaselineResidualDebt - storyResidualDebt);

  // ── Page numbering: pages render only when their story beat has real data ──
  const storyOk = hasComparisonPages && !!narrativeId && !!storyExtras && storyTable.length > 0;
  let __pn = 1;
  const debtPageNum        = hasComparisonPages ? ++__pn : null;
  const enginePageNum      = ++__pn;
  const yearOnePageNum     = storyOk ? ++__pn : null;
  const autopilotPageNum   = storyOk ? ++__pn : null;
  const payoffPageNum      = hasComparisonPages ? ++__pn : null;
  const whyJessePageNum    = ++__pn;
  const disclosuresPageNum = ++__pn;
  const totalPages = disclosuresPageNum;

  return (
    <div className="print-pages-container">
      <div className="pp-screen-header">
        <h2>Your Custom Convert Engine Plan — Print Preview</h2>
        <p>Review all {totalPages} pages below, then click <strong>Print Plan</strong> above to print or save as PDF.</p>
      </div>

      {/* PAGE 1 — COVER */}
      <Page num={1} total={totalPages} clientName={fullNames} cover>
        <div className="pp-cover-header-bar">
          <div className="pp-cover-logo">JBF</div>
          <div className="pp-cover-firm-header">J Bradin Financial</div>
        </div>
        <div className="pp-cover-inner">
          <div className="pp-cover-top-rule" />
          <div className="pp-cover-mid">
            <div className="pp-cover-doc-label">{ltfiEdition ? 'Your Tax-Free Paycheck Plan' : 'Your Custom Convert Engine Plan'}</div>
            <div className="pp-cover-names">{fullNames}</div>
          </div>
          <div className="pp-cover-divider" />
          <div className="pp-cover-meta">
            <div className="pp-cover-meta-row">
              <span className="pp-cover-meta-label">Prepared by</span>
              <span className="pp-cover-meta-value">Jesse Bradin</span>
            </div>
            <div className="pp-cover-meta-row">
              <span className="pp-cover-meta-label">Date Prepared</span>
              <span className="pp-cover-meta-value">{printDate()}</span>
            </div>
          </div>
          {ltfiEdition ? (
            <div className="pp-cover-strip">
              <div className="pp-cover-strip-item">
                <div className="pp-cover-strip-val pp-strip-gold">{fmtD(storyTfliYear1)}</div>
                <div className="pp-cover-strip-lbl">Tax-Free, This Year</div>
              </div>
              <div className="pp-cover-strip-arrow">→</div>
              <div className="pp-cover-strip-item">
                <div className="pp-cover-strip-val pp-strip-gold">{fmtD(storyTfliSteady)}/yr</div>
                <div className="pp-cover-strip-lbl">While Your IRA Converts</div>
              </div>
              <div className="pp-cover-strip-arrow">→</div>
              <div className="pp-cover-strip-item">
                <div className="pp-cover-strip-val pp-strip-green">Up to {fmtDM(ltfiIncome)}/yr</div>
                <div className="pp-cover-strip-lbl">For Life† At Full Conversion</div>
              </div>
            </div>
          ) : (debtToday != null && storyEngineSize > 0) && (
            <div className="pp-cover-strip">
              <div className="pp-cover-strip-item">
                <div className="pp-cover-strip-val pp-strip-red">{fmtDM(debtToday)}</div>
                <div className="pp-cover-strip-lbl">Your IRA's Tax Debt Today</div>
              </div>
              <div className="pp-cover-strip-arrow">→</div>
              <div className="pp-cover-strip-item">
                <div className="pp-cover-strip-val pp-strip-gold">{fmtDM(storyEngineSize)}</div>
                <div className="pp-cover-strip-lbl">The Roth Engine That Pays It</div>
              </div>
              <div className="pp-cover-strip-arrow">→</div>
              <div className="pp-cover-strip-item">
                <div className="pp-cover-strip-val pp-strip-green">$0</div>
                <div className="pp-cover-strip-lbl">Checks To The IRS From Year {storyZeroFromYr} On</div>
              </div>
            </div>
          )}
          <div className="pp-cover-tagline">
            {ltfiEdition
              ? <>"Two Roth engines. One is designed to start paying you this year. The other pays the IRS — until your whole IRA is tax-free."</>
              : storyExtList.length === 0
              ? <>"Your IRA owes a tax debt to the IRS. This plan shows how a small Roth engine pays all of it — and converts your whole IRA to tax-free — without you writing a check to the IRS."</>
              : storyExtList.length === 1
              ? <>"Your IRA owes a tax debt to the IRS. One decision — and one small check, your last ever — starts the engine that pays all the rest and converts your whole IRA to tax-free."</>
              : <>"Your IRA owes a tax debt to the IRS. A small out-of-pocket start — then the engine writes every check until your whole IRA is tax-free."</>}
          </div>
          {ltfiEdition && (
            <div style={{ fontSize: '8px', color: '#8a94a8', marginTop: '14px', lineHeight: 1.4, textAlign: 'center' }}>{LIFE_FN}</div>
          )}
        </div>
        <div className="pp-cover-footer">
          <span>J Bradin Financial — Confidential</span>
          <span>Page 1 of {totalPages}</span>
        </div>
      </Page>

      {/* PAGE 2 — YOUR IRA'S TAX DEBT */}
      {debtPageNum && (() => {
        const nameStr = (form.includeSpouse && form.spouseFirstName)
          ? `${form.clientFirstName} and ${form.spouseFirstName}` : form.clientFirstName || clientName;
        const preTaxTotal = num(form.balanceIRA) + num(form.balance401k);
        const claimData = storyBRows.map(r => ({ age: r.age, Claim: Math.round(r.iraDeferredTax || 0) }));
        const rentalYr   = Math.round(num(form.pensionMonthly) * 12);
        const ssClaimAge = num(narrativeScen?.ssClaimingAge) || num(form.clientSSClaimingAge) || 67;
        // Engine-true SS at the claim age (claim factor + COLA applied) — NOT the stated
        // FRA-basis amount. Rounded to the nearest $100 for narrative use.
        const ssRowAtClaim = storyTable.find(r => r.age === ssClaimAge);
        const ssYr = Math.round((num(ssRowAtClaim?.ssIncome) || num(form.clientSSMonthly) * 12) / 100) * 100;
        const expensesYr = Math.round(num(form.monthlyExpenses) * 12);
        return (
          <Page num={debtPageNum} total={totalPages} clientName={fullNames} autoHeight>
            <SectionTitle>{ltfiEdition ? 'Where Your Retirement Paycheck Comes From' : "Your IRA's Tax Debt"}</SectionTitle>
            {clientGoalsText && (
              <div style={{ background: '#f8f9fc', borderLeft: '4px solid #C9A84C', borderRadius: '4px', padding: '14px 18px', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Your Goals</div>
                <div style={{ fontSize: '13px', color: '#1B2B5E', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{clientGoalsText}</div>
              </div>
            )}
            {ltfiEdition ? (
              <div className="pp-narrative-box">
                <p className="pp-narrative-p"><strong>{nameStr}</strong>, here is your retirement income today:{rentalYr > 0 ? <> about <strong>{fmtD(rentalYr)}</strong> of rental income that moves with vacancies and repairs{ssYr > 0 ? ',' : ''}</> : null}{ssYr > 0 ? <> Social Security of about <strong>{fmtD(ssYr)}</strong> starting at {ssClaimAge}</> : null} — and no pension.{expensesYr > 0 ? <> Against roughly <strong>{fmtD(expensesYr)}</strong> a year of living expenses, your paycheck is thinner than your <strong>{fmtDM(totalAssets)}</strong> balance sheet suggests.</> : null}</p>
                <p className="pp-narrative-p">Your largest asset — the <strong>{fmtDM(preTaxTotal)}</strong> IRA — pays you nothing today. Worse: every dollar that ever comes out is taxed, and the IRS has already scheduled the withdrawals for you.</p>
              </div>
            ) : (
              <div className="pp-narrative-box">
                <p className="pp-narrative-p"><strong>{nameStr}</strong>, you have built <strong>{fmtDM(totalAssets)}</strong> across your retirement accounts{preTaxTotal > 0 ? <> — <strong>{fmtDM(preTaxTotal)}</strong> of it pre-tax</> : null}.</p>
                <p className="pp-narrative-p">Every pre-tax dollar carries a debt to the IRS. You just haven't paid it yet — and the debt grows every year your balance grows.</p>
              </div>
            )}
            {debtToday != null && (
              <div className="pp-debt-hero">
                <div className="pp-debt-hero-label">The IRS's Claim On Your IRA Today</div>
                <div className="pp-debt-hero-value">{fmtDM(debtToday)}</div>
                {storyRmdAge != null && (
                  debtAtRmd != null && debtAtRmd > debtToday
                    ? <div className="pp-debt-hero-sub">Left alone, the claim grows to <strong>{fmtDM(debtAtRmd)}</strong> by age {storyRmdAge} — then the IRS starts collecting by forcing you to take RMDs.</div>
                    : <div className="pp-debt-hero-sub">And at age {storyRmdAge}, the IRS starts collecting it by forcing you to take RMDs.</div>
                )}
              </div>
            )}
            <div className="pp-traj-chart-wrap">
              <ResponsiveContainer width="100%" height={ltfiEdition ? 185 : 230}>
                <AreaChart data={claimData} margin={{ top: 24, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="age" />
                  <YAxis tickFormatter={fmtChartY} width={75} />
                  <Tooltip formatter={(v) => fmtChartY(v)} />
                  {storyRmdAge != null && (
                    <ReferenceLine x={storyRmdAge} stroke="#b91c1c" strokeDasharray="4 4" label={{ value: 'RMDs Begin', position: 'top', fontSize: 11, fill: '#b91c1c' }} />
                  )}
                  <Area type="monotone" dataKey="Claim" name="IRS claim on your IRA" stroke="#dc2626" fill="#fecaca" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {firstBaselineRMD != null && (
              <div className="pp-debt-forced">
                At age {storyRmdAge}, Required Minimum Distributions begin. Your first forced withdrawal is an estimated <strong>{fmtD(firstBaselineRMD)}</strong> — fully taxable, whether you need the money or not, at whatever rates Congress sets that year.
              </div>
            )}
            <div className="pp-debt-kicker">
              {ltfiEdition
                ? <>You don't have an income plan yet — you have a tax deferral with a deadline. This plan is designed to turn that IRA into the paycheck: tax-free, on your schedule, starting this year. The next page shows the two engines that do it.</>
                : storyExtList.length === 0
                ? <>This plan retires that debt — without you writing the checks. The next page shows the machine that does it.</>
                : <>This plan retires that debt — one small check from you, the engine pays the rest. The next page shows the machine that does it.</>}
            </div>
            <div className="pp-yby-footnote" style={{ marginTop: '10px' }}>
              The claim shown is the modeled tax your heirs would pay on an inherited IRA (10-year payout at their rates) — the same basis used for Legacy throughout this plan.
            </div>
          </Page>
        );
      })()}

      {/* PAGE 3 — THE ENGINE THAT PAYS IT */}
      <Page num={enginePageNum} total={totalPages} clientName={fullNames} autoHeight>
        <SectionTitle>{ltfiEdition ? 'Two Engines, One Plan' : 'The Engine That Pays It — Small By Design'}</SectionTitle>
        {!narrativeScen ? (
          <div className="pp-no-scenarios">No Convert Engine scenario has been configured. Add one in Section 6 of the input form.</div>
        ) : (() => {
          const scenario = narrativeScen;
          const iraTotal = num(form.balanceIRA) + num(form.balance401k);
          const ceTarget = num(scenario.ceTargetSize);
          // Year-1 conversion = the actual computed value from the CE annual table
          // (what the schedule/engine use), NOT the stale scenario.year1Amount input.
          const year1Amt = num(storyConvRows[0]?.conversionAmount)
            || num(scenario.year1Amount) || num(scenario.conversionAmount);
          const annualAmt = num(scenario.conversionAmount);
          const _laterAmts = storyConvRows.slice(1).map(r => num(r.conversionAmount));
          const _loAmt = _laterAmts.length ? Math.min(..._laterAmts) : 0;
          const _hiAmt = _laterAmts.length ? Math.max(..._laterAmts) : 0;
          // Years 2+ conversion label. The engine ROWS are the source of truth — a scenario
          // driven by a per-year conversionSchedule can still carry a stale flat
          // scenario.conversionAmount, and printing that field advertised a number the plan
          // never converts. Flat scenarios are unaffected: every later row equals annualAmt,
          // so _loAmt === _hiAmt and this renders the same string as before.
          const _laterAmtLabel = _laterAmts.length
            ? (_loAmt === _hiAmt ? fmtD(_hiAmt) : `${fmtD(_loAmt)}–${fmtD(_hiAmt)}`)
            : fmtD(annualAmt);
          // Only claim the conversions "step up" when they actually do, monotonically.
          const _laterStepsUp = _laterAmts.length > 1
            && _laterAmts.every((a, i) => i === 0 || a >= _laterAmts[i - 1]);
          const seedAmt = ceTarget > 0 ? ceTarget : year1Amt;   // engine SIZE = target when set
          const existingRoth = num(form.balanceRothIRA);
          const topUp = Math.max(0, seedAmt - existingRoth);
          const ceFundedYear = storyExtras?.ceFundedYear ?? null;
          // Year-1 dollars available to the CE/Std-Roth split = conversion minus the TFLI
          // carve-out (the engine routes the TFLI slice first). 0 carve-out ⇒ unchanged.
          const y1ForBuckets = Math.max(0, year1Amt - (storyHasTfli ? storyTfliAllocY1 : 0));
          const topUpNeeded = Math.max(0, ceTarget - existingRoth);
          const year1TopUp  = Math.min(y1ForBuckets, topUpNeeded);
          const year1ToStd  = Math.max(0, y1ForBuckets - year1TopUp);
          const fullInYear1 = ceTarget > 0 && (ceFundedYear === 1 || (existingRoth + year1TopUp) >= ceTarget - 1);
          const jbfTaxFunded = storyExtras?.totalJBFTaxFunded ?? 0;
          const tileTopUp = ceTarget > 0 ? Math.min(y1ForBuckets, Math.max(0, ceTarget - existingRoth)) : y1ForBuckets;
          const tileToStd = Math.max(0, y1ForBuckets - tileTopUp);
          const schedClientAge = storyTable[0]?.age ?? null;
          const schedLastAge   = storyConvRows.length ? storyConvRows[storyConvRows.length - 1].age : null;
          const schedLastYear  = (schedClientAge != null && schedLastAge != null)
            ? (schedLastAge - schedClientAge + 1) : num(scenario.endYear);
          const schedSecondAge = storyConvRows.length > 1 ? storyConvRows[1].age : null;
          const yrs2Amts       = storyConvRows.slice(1).map(r => Math.round(num(r.conversionAmount)));
          const schedLevel2    = yrs2Amts.length ? (yrs2Amts.every(a => a === yrs2Amts[0]) ? yrs2Amts[0] : null) : null;
          const displayTotalConverted = storyConvRows.reduce((s, r) => s + num(r.conversionAmount), 0);
          let narrativeParas;
          if (ceTarget > 0) {
            narrativeParas = <>
              <p className="pp-narrative-p">Your Convert Engine is sized to <strong>{fmtDM(ceTarget)}</strong>.</p>
              {fullInYear1 ? (
                <p className="pp-narrative-p">In Year 1 you convert <strong>{fmtD(year1Amt)}</strong> — <strong>{fmtD(year1TopUp)}</strong> tops up your Convert Engine to <strong>{fmtDM(ceTarget)}</strong>{existingRoth > 0 ? <> (with your existing <strong>{fmtD(existingRoth)}</strong> Roth)</> : null}{year1ToStd > 0 ? <>, and the remaining <strong>{fmtD(year1ToStd)}</strong> goes straight into your Standard Roth</> : null}.</p>
              ) : (
                <p className="pp-narrative-p">In Year 1 you convert <strong>{fmtD(year1Amt)}</strong>{existingRoth > 0 ? <> on top of your existing <strong>{fmtD(existingRoth)}</strong> Roth</> : null}, then continue converting <strong>{_laterAmtLabel}</strong> per year{_laterStepsUp && _loAmt !== _hiAmt ? (storyHasTfli ? ' — front-loaded to build your TFLI sleeve first —' : ' — stepping up as the engine grows —') : ''} until the engine reaches <strong>{fmtDM(ceTarget)}</strong>{ceFundedYear ? <> — fully funded by Year {ceFundedYear}</> : null}. Every conversion beyond the target flows straight into your Standard Roth.</p>
              )}
              <p className="pp-narrative-p">{fullInYear1 ? 'From Year 2 on' : 'Once the engine is full'}, the engine's own distributions pay the conversion taxes — you never write another check to the IRS for them.</p>
            </>;
          } else if (existingRoth >= seedAmt) {
            narrativeParas = <>
              <p className="pp-narrative-p">You already have <strong>{fmtDM(existingRoth)}</strong> in your Roth IRA — enough to fund your Convert Engine without any additional seed conversion.</p>
              <p className="pp-narrative-p">We put your existing Roth to work immediately.</p>
              <p className="pp-narrative-p">Year 1 distributions begin covering conversion taxes from day one. No seed conversion necessary.</p>
            </>;
          } else if (existingRoth > 0) {
            narrativeParas = <>
              <p className="pp-narrative-p">You currently have <strong>{fmtDM(existingRoth)}</strong> in your Roth IRA.</p>
              <p className="pp-narrative-p">Your Convert Engine requires <strong>{fmtDM(seedAmt)}</strong> in seed capital.</p>
              <p className="pp-narrative-p">We convert <strong>{fmtDM(topUp)}</strong> from your IRA in Year 1 to top it up.</p>
              <p className="pp-narrative-p">From that point forward, the CE portfolio distributions cover every future conversion tax using tax-free dollars you already own.</p>
            </>;
          } else {
            narrativeParas = <>
              <p className="pp-narrative-p">You currently have no Roth IRA.</p>
              <p className="pp-narrative-p">Your Convert Engine requires <strong>{fmtDM(seedAmt)}</strong> in seed capital.</p>
              <p className="pp-narrative-p">In Year 1 we convert <strong>{fmtDM(seedAmt)}</strong> from your IRA to establish it.</p>
              <p className="pp-narrative-p">That is the last conversion tax you fund from outside. Every conversion after that is paid by the CE portfolio itself.</p>
            </>;
          }
          return (
            <>
              {ltfiEdition ? (
                <div className="pp-narrative-box">
                  <p className="pp-narrative-p">You could convert your whole <strong>{fmtDM(iraTotal)}</strong> IRA tomorrow and pay {storyTotalConvTax > 0 ? <>the <strong>{fmtD(storyTotalConvTax)}</strong> of tax</> : <>all that tax</>} from savings. Almost nobody does. The fix is one Roth account doing <strong>two jobs at once</strong>:</p>
                </div>
              ) : (
                <div className="pp-narrative-box">
                  <p className="pp-narrative-p">You could convert your whole <strong>{fmtDM(iraTotal)}</strong> IRA tomorrow and pay {storyTotalConvTax > 0 ? <>the <strong>{fmtD(storyTotalConvTax)}</strong> of tax</> : <>all that tax</>} from your savings. Almost nobody does — nobody willingly writes checks that size to the IRS, so the debt just sits there and grows.</p>
                  <p className="pp-narrative-p">The fix is a small Roth income portfolio — your <strong>Convert Engine</strong> — earning tax-free income that pays the conversion tax from inside your own account.</p>
                </div>
              )}
              {ltfiEdition && (
                <div className="pp-print-exact" style={{ display: 'flex', gap: '12px', margin: '12px 0', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                  <div style={{ flex: 1, background: '#1B2B5E', borderRadius: '8px', padding: '16px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>Your Paycheck Engine</div>
                    <div style={{ fontSize: '8px', color: '#9aa5c4', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '2px' }}>Your Tax-Free Lifetime Income (TFLI) account</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#ffffff', margin: '6px 0 2px' }}>{fmtD(storyTfliBucket)}</div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#C9A84C' }}>Pays YOU</div>
                    <div style={{ fontSize: '11px', color: '#e5e7eb', marginTop: '6px', lineHeight: 1.4 }}>{fmtD(storyTfliYear1)} this year, then <strong>{fmtD(storyTfliSteady)}/yr</strong> tax-free, for life†</div>
                    <div style={{ fontSize: '11px', color: '#C9A84C', marginTop: '4px', fontWeight: 700 }}>{fmtDM(storyTfliPaid)} paid to you by age {lifeExpectancy}†</div>
                  </div>
                  <div style={{ flex: 1, background: '#f8f9fc', border: '1px solid #C9A84C', borderRadius: '8px', padding: '16px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#1B2B5E', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>Your Convert Engine</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#1B2B5E', margin: '6px 0 2px' }}>{fmtD(ceTarget > 0 ? ceTarget : storyEngineSize)}</div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#b45309' }}>Pays the IRS</div>
                    <div style={{ fontSize: '11px', color: '#374151', marginTop: '6px', lineHeight: 1.4 }}><strong>{fmtD(jbfTaxFunded)}</strong> of conversion tax — checks you never write</div>
                  </div>
                </div>
              )}
              {!ltfiEdition && storyEngineSize > 0 && iraTotal > 0 && (() => {
                const big = 150;
                const small = Math.max(48, Math.round(big * Math.sqrt(Math.min(1, storyEngineSize / iraTotal))));
                return (
                  <div className="pp-circles">
                    <div className="pp-circles-item">
                      <div className="pp-circle pp-circle-ira" style={{ width: `${big}px`, height: `${big}px` }} />
                      <div className="pp-circles-val">{fmtDM(iraTotal)}</div>
                      <div className="pp-circles-lbl">Your pre-tax IRA / 401(k)<br/>carrying the debt</div>
                    </div>
                    <div className="pp-circles-vs">is paid off by</div>
                    <div className="pp-circles-item">
                      <div className="pp-circle pp-circle-engine" style={{ width: `${small}px`, height: `${small}px` }} />
                      <div className="pp-circles-val pp-circles-val-gold">{fmtDM(storyEngineSize)}</div>
                      <div className="pp-circles-lbl">Your Convert Engine —<br/>a small Roth income portfolio</div>
                      <div className="pp-circles-callout">
                        {storyExtList.length === 0
                          ? <>→ Your cost to start it: <strong>$0</strong> — the engine pays every check.</>
                          : storyExtList.length === 1
                          ? <>→ Your cost to start it: <strong>{fmtD(storyCostToStart)}</strong> — the only check you ever write.</>
                          : <>→ Your out-of-pocket start: <strong>{fmtD(Math.round(storyExtras?.totalExternalTaxPaid ?? 0))}</strong> — the engine pays the rest.</>}
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="pp-narrative-box" style={{ marginTop: '14px' }}>
                <div className="pp-col-header" style={{ marginBottom: '6px' }}>{ltfiEdition ? 'How We Size Your Engines' : 'How We Size Your Convert Engine'}</div>
                {ltfiEdition ? (() => {
                  const conv2  = Math.round(num(storyConvRows[1]?.conversionAmount) || 0);
                  const y2ToCE = Math.max(0, conv2 - storyTfliAllocY2);
                  return (
                    <>
                      <p className="pp-narrative-p">In Year 1 you convert <strong>{fmtD(year1Amt)}</strong>: <strong>{fmtD(storyTfliAllocY1)}</strong> builds your Paycheck Engine — your income is designed to start immediately — and <strong>{fmtD(tileTopUp)}</strong> seeds your Convert Engine.</p>
                      {storyTfliAllocY2 > 0 && conv2 > 0 && (
                        <p className="pp-narrative-p">Year 2 repeats: <strong>{fmtD(storyTfliAllocY2)}</strong> finishes the Paycheck Engine at <strong>{fmtD(storyTfliBucket)}</strong>, and <strong>{fmtD(y2ToCE)}</strong> grows the Convert Engine.</p>
                      )}
                      <p className="pp-narrative-p">From there, steady conversions fill the engine to <strong>{fmtDM(ceTarget > 0 ? ceTarget : storyEngineSize)}</strong>{ceFundedYear ? <> — fully funded by Year {ceFundedYear}</> : null} — and its own distributions are modeled to pay every remaining conversion tax until your IRA reaches $0 at age {storyDebtPaidAge}{storyRmdAge != null && storyDebtPaidAge != null && storyDebtPaidAge < storyRmdAge ? <> — before RMDs would ever have begun</> : null}.</p>
                    </>
                  );
                })() : narrativeParas}
              </div>
              <div className="pp-scenario-meta-grid" style={{ marginTop: '14px' }}>
                <div className="pp-smeta-item"><span>ENGINE SIZE</span><strong>{fmtD(ceTarget > 0 ? ceTarget : year1Amt)}</strong></div>
                <div className="pp-smeta-item"><span>CURRENT ROTH</span><strong>{fmtD(existingRoth)}</strong></div>
                <div className="pp-smeta-item">
                  <span>YEAR 1 CONVERSION</span>
                  <strong>{fmtD(year1Amt)}{(ceTarget > 0 || (storyHasTfli && storyTfliAllocY1 > 0)) ? <><br/><span style={{ fontSize: '10px', fontWeight: 400, color: '#6b7280' }}>{fmtD(tileTopUp)} → CE{storyHasTfli && storyTfliAllocY1 > 0 ? <> · {fmtD(storyTfliAllocY1)} → Paycheck Engine</> : null}{tileToStd > 0 ? <> · {fmtD(tileToStd)} → Standard Roth</> : null}</span></> : null}</strong>
                </div>
                <div className="pp-smeta-item">
                  <span>{schedSecondAge != null ? `AGES ${schedSecondAge}–${schedLastAge}` : `YEARS 2–${schedLastYear}`}</span>
                  <strong>{schedLevel2 != null ? <>Convert {fmtD(schedLevel2)}/yr</> : <>Conversions vary by year</>}<br/><span style={{ fontSize: '10px', fontWeight: 400, color: '#6b7280' }}>{ceFundedYear ? `CE full by Year ${ceFundedYear}, then → Standard Roth` : 'CE Pays All Taxes'}</span></strong>
                </div>
                <div className="pp-smeta-item"><span>TOTAL CONVERTED</span><strong>{fmtDM(displayTotalConverted)}</strong></div>
                <div className="pp-smeta-item">
                  <span>CE PAYS EST. TAXES</span>
                  <strong className={jbfTaxFunded > 0 ? 'pp-smeta-zero' : ''}>{fmtD(jbfTaxFunded)}</strong>
                </div>
                {storyHasTfli && (
                  <div className="pp-smeta-item">
                    <span>PAYCHECK ENGINE (TFLI)</span>
                    <strong>{fmtD(storyTfliBucket)}<br/><span style={{ fontSize: '10px', fontWeight: 400, color: '#6b7280' }}>designed to pay you {fmtD(storyTfliSteady)}/yr tax-free, for life†</span></strong>
                  </div>
                )}
              </div>
              {storyHasTfli && !ltfiEdition && (
                <div className="pp-narrative-box" style={{ marginTop: '14px' }}>
                  <div className="pp-col-header" style={{ marginBottom: '6px' }}>Your Paycheck Engine — Tax-Free Lifetime Income That Starts Now</div>
                  <p className="pp-narrative-p">Your first conversions also build a second Roth portfolio — your <strong>{fmtD(storyTfliBucket)} Paycheck Engine</strong>, a Tax-Free Lifetime Income (TFLI) account. It is designed to start paying you immediately: <strong>{fmtD(storyTfliYear1)}</strong> of tax-free income in Year 1, rising to <strong>{fmtD(storyTfliSteady)}/yr</strong>{storyTfliPlateauAge != null ? <> from age {storyTfliPlateauAge}</> : null} — modeled to continue for life† while the Convert Engine pays the IRS.</p>
                  <LifeFootnote />
                </div>
              )}
              {ltfiEdition && (
                <div className="pp-debt-kicker" style={{ marginTop: '12px' }}>Same Roth. Two jobs. Every dollar working tax-free.</div>
              )}
              {form.riskScore && (() => {
                const rs = form.riskScore;
                const subs = [
                  { label: 'Loss Tolerance', val: rs.lossToleranceScore },
                  { label: 'Capacity', val: rs.riskCapacityScore },
                  { label: 'Roth Conversion Context', val: rs.rothContextScore },
                ];
                const caption = (rs.narrative && rs.narrative.split('. ')[0].trim() + '.')
                  || 'This score reflects loss tolerance, capacity to take risk, and Roth conversion suitability. Your engine tier is set by it.';
                return (
                  <div style={{
                    marginTop: '16px',
                    background: '#f8f9fc',
                    borderLeft: '4px solid #C9A84C',
                    borderRadius: '4px',
                    padding: '14px 18px',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>{ltfiEdition ? 'Risk Profile — What Sized Your Engines' : 'Risk Profile — What Sized Your Engine'}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                      <div style={{ flex: '0 0 96px', textAlign: 'center' }}>
                        <div style={{ fontSize: '40px', fontWeight: 700, color: '#1B2B5E', lineHeight: 1 }}>{rs.composite}</div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#1B2B5E', marginTop: '4px' }}>{rs.band}</div>
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '9px' }}>
                        {subs.map(s => (
                          <div key={s.label}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '3px' }}>
                              <span style={{ color: '#1B2B5E', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {s.label}
                              </span>
                              <span style={{ color: '#1B2B5E', fontWeight: 700 }}>{s.val} <span style={{ color: '#9ca3af', fontWeight: 400 }}>/ 100</span></span>
                            </div>
                            <div className="pp-risk-track">
                              <div className="pp-risk-fill" style={{ width: `${Math.max(0, Math.min(100, s.val))}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {(() => {
                      const t = CE_TIERS[scenario.tier];
                      if (!t) return null;
                      const tierNum = { T1: 1, T2: 2, T3: 3 }[scenario.tier];
                      const rec = tierForRiskScore(rs.composite);
                      const overridden = rec != null && rec !== scenario.tier;
                      return (
                        <div className="pp-risk-tier-line">
                          Your score sets your engine: <strong>{t.label} — {t.sub}</strong> (Tier {tierNum} of 3){overridden ? ' · selected with your advisor' : ''}
                        </div>
                      );
                    })()}
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '10px', fontStyle: 'italic' }}>{caption}</div>
                  </div>
                );
              })()}
              {ltfiEdition && <LifeFootnote />}
            </>
          );
        })()}
      </Page>

      {/* PAGE 4 — YEAR ONE: YOUR ONE DECISION */}
      {yearOnePageNum && (() => {
        const existingRoth = num(form.balanceRothIRA);
        const year1Amt = num(storyConvRows[0]?.conversionAmount) || num(narrativeScen?.year1Amount) || num(narrativeScen?.conversionAmount);
        const ceTarget = num(narrativeScen?.ceTargetSize);
        // Year-1 dollars for the CE/Std split = conversion minus the TFLI carve-out.
        const y1ForBuckets = Math.max(0, year1Amt - (storyHasTfli ? storyTfliAllocY1 : 0));
        const topUpNeeded = ceTarget > 0 ? Math.max(0, ceTarget - existingRoth) : y1ForBuckets;
        const year1TopUp = Math.min(y1ForBuckets, topUpNeeded);
        const year1ToStd = Math.max(0, y1ForBuckets - year1TopUp);
        const laterYears = storyExtList.slice(1);
        const totalExt = Math.round(storyExtras?.totalExternalTaxPaid ?? 0);
        const sum = results.v2Summaries?.[narrativeId];
        const endOfConvRoth = storyExtras?.conversionCompleteRow?.rothBalance
          ?? ([...storyTable].reverse().find(r => num(r.conversionAmount) > 0)?.rothBalance ?? 0);
        const ltfiVal = ltfiPerYear(endOfConvRoth, narrativeScen?.ltfiYield);
        const seededByRoth = ceTarget > 0 && existingRoth >= ceTarget;
        return (
          <Page num={yearOnePageNum} total={totalPages} clientName={fullNames} autoHeight>
            <SectionTitle>{ltfiEdition ? 'Year One — Your Paycheck Starts' : <>Year One — Approve the Plan &amp; Start the Engine</>}</SectionTitle>
            <div className="pp-narrative-box">
              <p className="pp-narrative-p">Here is exactly what happens in the first year — who pays what, and what it costs you.</p>
            </div>
            <div className="pp-yearone-steps">
              <div className="pp-yearone-step">
                <div className="pp-yearone-num">1</div>
                <div className="pp-yearone-content">
                  <div className="pp-yearone-hdr">{ltfiEdition ? 'Approve the plan — we start both engines' : 'Approve the plan — we start the engine'}</div>
                  <div className="pp-yearone-body">
                    {seededByRoth
                      ? <>Your existing <strong>{fmtDM(existingRoth)}</strong> Roth funds the engine on day one — no seed conversion needed. It starts earning tax-free income immediately.</>
                      : <>In Year 1 you convert <strong>{fmtD(year1Amt)}</strong>{existingRoth > 0 ? <> on top of your existing <strong>{fmtD(existingRoth)}</strong> Roth</> : null}{ceTarget > 0 || (storyHasTfli && storyTfliAllocY1 > 0) ? <> — <strong>{fmtD(year1TopUp)}</strong> fills your Convert Engine{storyHasTfli && storyTfliAllocY1 > 0 ? <>, <strong>{fmtD(storyTfliAllocY1)}</strong> starts your Paycheck Engine — designed to pay you <strong>{fmtD(storyTfliYear1)}</strong> of tax-free income this year</> : null}{year1ToStd > 0 ? <>, and <strong>{fmtD(year1ToStd)}</strong> goes straight to your Standard Roth</> : null}</> : null}. One approval. We handle the rest.</>}
                  </div>
                </div>
              </div>
              {ltfiEdition ? (() => {
                const parts = storyExtList.map(x => `${fmtD(x.amt)} in Year ${x.yr}`);
                const last = storyExtList[storyExtList.length - 1];
                const costSentence = storyExtList.length > 1
                  ? `${parts.slice(0, -1).join(', ')}, and just ${fmtD(last.amt)} in Year ${last.yr}`
                  : (parts[0] || null);
                const jbfFunded4 = Math.round(storyExtras?.totalJBFTaxFunded || 0);
                const totalOut = jbfFunded4 + storyTfliPaid;
                return (
                  <>
                    <div className="pp-yearone-step">
                      <div className="pp-yearone-num">2</div>
                      <div className="pp-yearone-content">
                        <div className="pp-yearone-hdr">Cost to Complete</div>
                        <div className="pp-yearone-cost">{totalExt > 0 ? fmtD(totalExt) : '$0'}</div>
                        <div className="pp-yearone-body">
                          {costSentence
                            ? <>{costSentence}. From Year {storyZeroFromYr} on, the Convert Engine pays every conversion tax{num(form.balanceOther) > 0 ? <> — and your checks are funded from your <strong>{fmtD(num(form.balanceOther))}</strong> brokerage, with your lifestyle fully covered in the model</> : null}.</>
                            : <>The Convert Engine pays every conversion tax — nothing out of pocket.</>}
                        </div>
                      </div>
                    </div>
                    <div className="pp-yearone-step">
                      <div className="pp-yearone-num">3</div>
                      <div className="pp-yearone-content">
                        <div className="pp-yearone-hdr">Who pays — and who gets paid</div>
                        <div className="pp-yearone-body">
                          You pay <strong>{fmtD(totalExt)}</strong>. The Convert Engine pays <strong>{fmtD(jbfFunded4)}</strong> to the IRS for you. Your Paycheck Engine pays you <strong>{fmtDM(storyTfliPaid)}†</strong>. {fmtDM(totalExt)} in — <strong>{fmtDM(totalOut)}</strong> out, all of it modeled in your favor.
                        </div>
                      </div>
                    </div>
                  </>
                );
              })() : (
                <>
                  <div className="pp-yearone-step">
                    <div className="pp-yearone-num">2</div>
                    <div className="pp-yearone-content">
                      <div className="pp-yearone-hdr">Your cost to start</div>
                      <div className="pp-yearone-cost">{storyCostToStart > 0 ? fmtD(storyCostToStart) : '$0'}</div>
                      <div className="pp-yearone-body">
                        {storyCostToStart > 0
                          ? <>One time, in Year 1 — after the engine's own first-year income covers the rest of the bill.</>
                          : <>Your engine covers Year 1's conversion tax in full. Nothing out of pocket.</>}
                      </div>
                    </div>
                  </div>
                  <div className="pp-yearone-step">
                    <div className="pp-yearone-num">3</div>
                    <div className="pp-yearone-content">
                      <div className="pp-yearone-hdr">
                        {storyExtList.length === 0
                          ? 'You write $0 checks — the engine pays them all'
                          : storyExtList.length === 1
                          ? "That's the last check you write"
                          : 'Then the engine takes over'}
                      </div>
                      <div className="pp-yearone-body">
                        {laterYears.length > 0
                          ? <>Then {laterYears.map(x => `${fmtD(x.amt)} in Year ${x.yr}`).join(', ')} — and <strong>$0 from Year {storyZeroFromYr} on</strong>. Total out-of-pocket: <strong>{fmtD(totalExt)}</strong>; the engine pays everything else.</>
                          : <>From Year {storyZeroFromYr} on, every conversion's tax is paid by the engine's tax-free income — <strong>$0 from your pocket</strong>, every year, {storyFullConversion ? 'until your IRA is fully converted' : 'for every scheduled conversion'}.</>}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {sum && (
              <div style={{ border: '1px solid #C9A84C', borderRadius: '8px', padding: '12px 14px', marginTop: '18px', background: '#f8f9fc', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, color: '#1B2B5E', marginBottom: '8px' }}>
                  {ltfiEdition
                    ? <>Does this plan turn your IRA into a tax-free paycheck? — <span style={{ color: '#059669' }}>YES</span>
                        <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '11px' }}>{'  ·  '}and it {storyFullConversion ? 'retires' : 'cuts'} the IRA's tax debt on the way</span></>
                    : <>Does this plan {storyFullConversion ? "pay off" : "cut"} your IRA's tax debt? — <span style={{ color: '#059669' }}>YES</span>
                        <span style={{ fontWeight: 400, color: '#6b7280' }}>{'  ·  '}Goal: {goalLean === 'ltfi' ? 'Tax-Free Lifetime Income' : 'Legacy'}</span></>}
                </div>
                {[
                  ...(goalLean === 'ltfi'
                    ? (storyHasTfli
                        ? [
                            { label: 'Tax-Free Income — Available This Year', val: `$${storyTfliYear1.toLocaleString()}/yr`, lead: true },
                            { label: `Steady During Conversion${storyTfliPlateauAge != null && storyTfliLastConvAge != null ? ` (ages ${storyTfliPlateauAge}–${storyTfliLastConvAge})` : ''}`, val: `$${storyTfliSteady.toLocaleString()}/yr`, lead: true },
                            { label: `Maximum at Full Conversion${storyTfliLastConvAge != null ? ` (age ${storyTfliLastConvAge + 1}+)` : ''}`, val: `Up to $${ltfiVal.toLocaleString()}/yr`, lead: true },
                          ]
                        : [{ label: 'Tax-Free Lifetime Income', val: `$${ltfiVal.toLocaleString()}/yr`, lead: true }])
                    : [{ label: 'Legacy Advantage', val: `${sum.legacyDelta >= 0 ? '+' : '−'}${fmtDM(Math.abs(sum.legacyDelta))}`, lead: true }]),
                  { label: 'Lifetime Tax Savings', val: `${sum.totalTaxDelta >= 0 ? '+' : ''}${fmtDM(sum.totalTaxDelta)}` },
                  { label: 'RMDs Eliminated', val: fmtDM(sum.totalRMDDelta) },
                  // LTFI edition: the money-flow lines live in steps 2-3 above — keeping them
                  // here too was the page's repetition problem. Goal metrics only.
                  ...(ltfiEdition ? [] : [
                    { label: 'Paid by your Convert Engine (from inside your account)', val: fmtDM(storyExtras?.totalJBFTaxFunded || 0) },
                    ...(storyHasTfli ? [{ label: 'Paid to You by your Paycheck Engine (cumulative, tax-free)', val: fmtDM(storyTfliPaid) }] : []),
                    { label: 'Your cost to start this year', val: `$${(storyCostToStart || 0).toLocaleString()}` },
                  ]),
                ].map((ln, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 8px', fontSize: '12px',
                    background: ln.lead ? '#fff' : 'transparent', border: ln.lead ? '1px solid rgba(201,168,76,0.4)' : 'none',
                    borderRadius: ln.lead ? '4px' : 0, marginBottom: '3px', fontWeight: ln.lead ? 700 : 400, color: '#1B2B5E' }}>
                    <span>{ln.label}</span><strong>{ln.val}</strong>
                  </div>
                ))}
                {(storyExtras?.selfConvertingBenefit || 0) > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '10px', fontStyle: 'italic', color: '#6b7280', lineHeight: 1.4 }}>
                    The Convert Engine pays the tax from inside the account with tax-free dollars, so 100 cents of every converted dollar lands in your Roth — about {fmtDM(storyExtras.selfConvertingBenefit)} extra over time.
                  </div>
                )}
              </div>
            )}
            {ltfiEdition && <LifeFootnote />}
          </Page>
        );
      })()}

      {/* PAGE 5 — AUTOPILOT: THE ENGINE PAYS. YOU DON'T. */}
      {autopilotPageNum && storyConvRows.length > 0 && (() => {
        const lastConvAge = storyConvRows[storyConvRows.length - 1].age;
        const shownRows = storyTable.filter(r => r.age <= lastConvAge);
        const totalEnginePaid = Math.round(storyExtras?.totalJBFTaxFunded
          ?? shownRows.reduce((s, r) => s + (r.jbfYieldUsedForTax || 0), 0));
        const totalYouWrite = Math.round(storyExtras?.totalExternalTaxPaid
          ?? shownRows.reduce((s, r) => s + (r.externalTaxPaid || 0), 0));
        const totalConv = Math.round(storyConvRows.reduce((s, r) => s + num(r.conversionAmount), 0));
        const firstExhaustedAge = storyTable.find(r => r.fundingExhausted)?.age ?? null;
        const totalTfliPaidConv = Math.round(shownRows.reduce((s, r) => s + (r.tfliIncome || 0), 0));
        return (
          <Page num={autopilotPageNum} total={totalPages} clientName={fullNames} autoHeight>
            <SectionTitle>{storyHasTfli ? 'The Convert Engine Pays the IRS. Your Paycheck Engine Pays You.' : "The Engine Pays. You Don't."}</SectionTitle>
            <div className="pp-yby-intro">Every conversion year of your plan — what converts, and who pays the tax. "You Write" is money out of your pocket to the IRS.{storyHasTfli ? ' "Pays You" is tax-free income your Paycheck Engine pays you that same year.' : ''}</div>
            {firstExhaustedAge != null && (
              <div style={{
                margin: '8px 0 10px', padding: '10px 14px', background: '#fef2f2',
                border: '1px solid #fca5a5', borderLeft: '4px solid #dc2626', borderRadius: '4px',
                fontSize: '11px', color: '#7f1d1d', lineHeight: 1.5,
              }}>
                <strong style={{ color: '#dc2626' }}>⚠ Underfunded from age {firstExhaustedAge}.</strong>{' '}
                In the flagged years, expenses and/or conversion tax exceed the assets available
                outside the IRA. The plan preserves the IRA rather than raiding it; the shortfall
                appears as an unmet income gap.
              </div>
            )}
            <table className="pp-auto-table">
              <thead>
                <tr>
                  <th>Year</th><th>Age</th><th>Conversion</th><th>Engine Pays</th><th className="pp-auto-you-th">You Write</th>{storyHasTfli && <th className="pp-auto-engine">Pays You</th>}<th>Roth Balance</th><th>IRA Remaining</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.map((r, i) => {
                  const youWrite = Math.round(r.externalTaxPaid || 0);
                  const enginePaid = Math.round(r.jbfYieldUsedForTax || 0);
                  const tfliPaid = Math.round(r.tfliIncome || 0);
                  return (
                    <tr key={r.age} className={i % 2 === 1 ? 'pp-auto-alt' : ''}>
                      <td>Year {i + 1}</td>
                      <td>{r.age}</td>
                      <td>{num(r.conversionAmount) > 0 ? fmtD(r.conversionAmount) : '—'}</td>
                      <td className="pp-auto-engine">{enginePaid > 0 ? fmtD(enginePaid) : '—'}</td>
                      <td className={youWrite > 0 ? 'pp-auto-you pp-auto-you-amt' : 'pp-auto-you pp-auto-you-zero'}>{youWrite > 0 ? fmtD(youWrite) : '$0'}</td>
                      {storyHasTfli && <td className="pp-auto-engine">{tfliPaid > 0 ? fmtD(tfliPaid) : '—'}</td>}
                      <td>{fmtDM(r.rothBalance)}</td>
                      <td>{fmtDM(r.iraBalance)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="pp-auto-totals">
                  <td colSpan={2}>Totals</td>
                  <td>{fmtDM(totalConv)}</td>
                  <td className="pp-auto-engine">{fmtDM(totalEnginePaid)}</td>
                  <td className="pp-auto-you pp-auto-you-zero">{totalYouWrite > 0 ? fmtD(totalYouWrite) : '$0'}</td>
                  {storyHasTfli && <td className="pp-auto-engine">{fmtDM(totalTfliPaidConv)}</td>}
                  <td colSpan={2}>&nbsp;</td>
                </tr>
              </tfoot>
            </table>
            <div className="pp-yby-footnote">
              IRA Remaining is the end-of-year balance after that year's conversion and growth. "Engine Pays" is tax-free income generated inside your Roth — it never touches your bank account or your taxable income.{storyHasTfli ? ' "Pays You" is your Paycheck Engine’s tax-free distribution — income you can spend, modeled to keep paying after the last conversion.' : ''}
            </div>
            <div className="pp-auto-kicker">
              {totalYouWrite > 0
                ? <>You write {fmtD(totalYouWrite)}. The engine pays {fmtDM(totalEnginePaid)}. </>
                : <>You write nothing. The engine pays {fmtDM(totalEnginePaid)}. </>}
              {storyHasTfli ? <>Your Paycheck Engine pays you {fmtDM(totalTfliPaidConv)} along the way. </> : null}
              {storyFullConversion
                ? <>Your IRA converts in full.</>
                : <>{fmtDM(totalConv)} of your IRA converts to tax-free.</>}
              {ltfiEdition ? <> And the paycheck doesn't stop there: <strong>{fmtD(storyTfliSteady)} a year†</strong>, modeled for life.</> : null}
            </div>
            {ltfiEdition && <LifeFootnote />}
          </Page>
        );
      })()}

      {/* PAGE 6 — DEBT PAID: WHAT YOU KEEP */}
      {payoffPageNum && (() => {
        const scenId = narrativeId || String(printScens[0] ?? validScenarioIds[0]);
        const sum = results.v2Summaries?.[scenId];
        if (!sum) return null;
        const scen = form.rothScenarios.find(s => String(s.id) === String(scenId));
        const eRows = results.v2ScenarioTables?.[scenId] || [];
        const chartData = eRows.map(r => ({
          age: r.age,
          'IRA (taxable)': Math.round(r.iraBalance || 0),
          'Roth (tax-free)': Math.round(r.rothBalance || 0),
        }));
        const legacyGain = sum.finalLegacy - sum.finalLegacyBaseline;
        const baselineTaxTotal = sum.totalTaxBaseline
          ?? results.v2BaselineTable?.reduce((a, r) => a + (r.totalTax || 0), 0) ?? 0;
        const taxSaved = baselineTaxTotal - sum.totalTaxScenario;
        const rmdElim = sum.totalRMDBaseline - sum.totalRMDScenario;
        const rmdsZero = (sum.totalRMDScenario || 0) === 0;
        const breakEvenAge = results.v2BreakEvenAges?.[scenId];
        const bFinalRow = results.v2BaselineTable?.at(-1);
        const eFinalRow = eRows.at(-1);
        const bFinalLegacy = bFinalRow ? (bFinalRow.legacy ?? calcAfterTaxLegacy(bFinalRow)) : null;
        const eFinalLegacy = eFinalRow ? (eFinalRow.legacy ?? calcAfterTaxLegacy(eFinalRow)) : sum.finalLegacy;
        const finalAge = bFinalRow?.age ?? lifeExpectancy;
        const ppYld = (parseFloat(ppJbfScen?.ltfiYield) || 6) / 100;
        const baseLTFI = Math.round((results.v2BaselineTable?.at(-1)?.rothBalance || 0) * ppYld);
        const isLtfi = goalLean === 'ltfi';
        const nameStr = (form.includeSpouse && form.spouseFirstName)
          ? `${form.clientFirstName} & ${form.spouseFirstName}'s` : (form.clientFirstName ? `${form.clientFirstName}'s` : 'Your');
        const jbfFunded = storyExtras?.totalJBFTaxFunded ?? 0;
        return (
          <Page num={payoffPageNum} total={totalPages} clientName={fullNames} autoHeight>
            <SectionTitle>{ltfiEdition
              ? `${nameStr} Paycheck Is Built To Last — And The IRS's Schedule Is Gone`
              : storyDebtPaidAge != null && storyFullConversion
              ? `${nameStr} Debt Hits $0 At Age ${storyDebtPaidAge}`
              : (storyDebtPaidAge != null && storyDebtCut > 0
                  ? `${nameStr} Debt Is Cut By ${fmtDM(storyDebtCut)}`
                  : `What You Keep At Age ${finalAge}`)}</SectionTitle>
            {ltfiEdition && (
              <div className="pp-print-exact" style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 12px', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                {[
                  { amt: storyTfliYear1, when: 'This Year' },
                  { amt: storyTfliSteady, when: storyTfliPlateauAge != null && storyTfliLastConvAge != null ? `Ages ${storyTfliPlateauAge}–${storyTfliLastConvAge}` : 'While Converting' },
                  { amt: ltfiIncome, when: storyTfliLastConvAge != null ? `Your Max Dial, Age ${storyTfliLastConvAge + 1}+†` : 'Your Max Dial, At Full Conversion†', upTo: true },
                ].map((t, i, arr) => (
                  <Fragment key={i}>
                    <div style={{ flex: 1, background: '#f8f9fc', border: '1px solid rgba(201,168,76,0.5)', borderRadius: '6px', padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '17px', fontWeight: 700, color: '#1B2B5E', whiteSpace: 'nowrap' }}>{t.upTo ? 'Up to ' : ''}${Math.round(t.amt).toLocaleString()}/yr</div>
                      <div style={{ fontSize: '9px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginTop: '3px' }}>Tax-Free · {t.when}</div>
                    </div>
                    {i < arr.length - 1 && <div style={{ fontSize: '18px', color: '#9ca3af', fontWeight: 300 }}>→</div>}
                  </Fragment>
                ))}
              </div>
            )}
            {storyDebtPaidAge != null && (storyFullConversion || storyDebtCut > 0) && (
              <div className="pp-debtpaid-row">
                <span className="pp-debtpaid-badge">{storyFullConversion ? 'DEBT PAID' : 'DEBT CUT'}</span>
                <span className="pp-debtpaid-text">
                  {!storyFullConversion
                    ? <>The IRS's modeled claim at age {finalAge}: <strong>{fmtDM(storyBaselineResidualDebt)}</strong> if you do nothing → <strong>{fmtDM(storyResidualDebt)}</strong> with this plan{rmdElim > 0 ? <> — and lifetime forced RMDs are cut by <strong>{fmtDM(rmdElim)}</strong></> : null}.</>
                    : rmdsZero
                    ? <>Required Minimum Distributions, at 73, 75, 80, 90: <strong>$0</strong>. The IRS no longer has a schedule for your money.</>
                    : <>Lifetime forced RMDs cut by <strong>{fmtDM(rmdElim)}</strong>.</>}
                </span>
              </div>
            )}
            <div className="pp-traj-chart-wrap">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 24, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="age" />
                  <YAxis tickFormatter={fmtChartY} width={75} />
                  <Tooltip formatter={(v) => fmtChartY(v)} />
                  <Legend />
                  {storyDebtPaidAge != null && (
                    <ReferenceLine x={storyDebtPaidAge} stroke="#4a9e6b" strokeDasharray="4 4" label={{ value: storyFullConversion ? 'Debt paid' : 'Conversions complete', position: 'top', fontSize: 11, fill: '#4a9e6b' }} />
                  )}
                  <Line type="monotone" dataKey="IRA (taxable)" stroke="#e87070" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="Roth (tax-free)" stroke="#C9A84C" dot={false} strokeWidth={2.5} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="pp-print-exact" style={{
              display: 'flex',
              alignItems: 'center',
              background: '#1B2B5E',
              borderRadius: '8px',
              padding: '22px 16px',
              margin: '16px 0',
            }}>
              <div style={{ flex: 1, textAlign: 'center', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', whiteSpace: 'nowrap', lineHeight: 1.1 }}>
                  {isLtfi ? <>{ltfiEdition ? 'Up to ' : '+'}{fmtDM(ltfiIncome)}/yr</> : <>{legacyGain >= 0 ? '+' : '−'}{fmtDM(Math.abs(legacyGain))}</>}
                </div>
                <div style={{ fontSize: '10px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '7px', fontWeight: 600 }}>{isLtfi ? (ltfiEdition ? 'Tax-Free Income For Life†' : 'Tax-Free Income For Life') : 'More For Your Family'}</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', whiteSpace: 'nowrap', lineHeight: 1.1 }}>
                  {taxSaved >= 0 ? '+' : '−'}{fmtDM(Math.abs(taxSaved))}
                </div>
                <div style={{ fontSize: '10px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '7px', fontWeight: 600 }}>Less To The IRS</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0 12px' }}>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', whiteSpace: 'nowrap', lineHeight: 1.1 }}>
                  {rmdElim >= 0 ? '+' : '−'}{fmtDM(Math.abs(rmdElim))}
                </div>
                <div style={{ fontSize: '10px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '7px', fontWeight: 600 }}>RMDs Eliminated</div>
              </div>
            </div>
            {breakEvenAge && (isLtfi ? (ltfiIncome - baseLTFI) : legacyGain) > 0 && (
              <div className="pp-breakeven-callout">
                By age {breakEvenAge}, you're ahead of doing nothing — and the gap only widens from there.
              </div>
            )}
            {(bFinalLegacy != null || isLtfi) && (
              <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ flex: 1, background: '#f8f9fc', borderRadius: '6px', padding: '12px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Doing Nothing, At Age {finalAge}</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#1B2B5E' }}>{isLtfi ? <>{fmtDM(baseLTFI)}/yr</> : fmtDM(bFinalLegacy)}</div>
                </div>
                <div style={{ fontSize: '22px', color: '#9ca3af', fontWeight: 300 }}>→</div>
                {ltfiEdition ? (
                  <>
                    <div className="pp-print-exact" style={{ flex: 1, background: '#1B2B5E', borderRadius: '6px', padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{scen?.name || 'Your Plan'}{storyTfliPlateauAge != null ? ` — From Age ${storyTfliPlateauAge}` : ' — Starting Now'}</div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff' }}>{fmtD(storyTfliSteady)}/yr</div>
                      <div style={{ fontSize: '10px', color: '#e5e7eb', marginTop: '3px' }}>tax-free, for life†</div>
                    </div>
                    <div style={{ fontSize: '22px', color: '#9ca3af', fontWeight: 300 }}>→</div>
                    <div className="pp-print-exact" style={{ flex: 1, background: '#C9A84C', borderRadius: '6px', padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#1B2B5E', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '4px' }}>Then The Dial Is In Your Hands</div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#1B2B5E' }}>Up to {fmtDM(ltfiIncome)}/yr</div>
                      <div style={{ fontSize: '10px', color: '#1B2B5E', marginTop: '3px' }}>tax-free income for life† — or legacy, your call</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pp-print-exact" style={{ flex: 1, background: '#1B2B5E', borderRadius: '6px', padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{scen?.name || 'Your Plan'}, At Age {finalAge}</div>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#ffffff' }}>{isLtfi ? <>{fmtDM(ltfiIncome)}/yr</> : fmtDM(eFinalLegacy)}</div>
                    </div>
                    <div style={{ fontSize: '22px', color: '#9ca3af', fontWeight: 300 }}>→</div>
                    {(() => {
                      const adv = isLtfi ? (ltfiIncome - baseLTFI) : legacyGain;
                      return (
                        <div className="pp-print-exact" style={{ flex: 1, background: '#C9A84C', borderRadius: '6px', padding: '12px 16px', textAlign: 'center' }}>
                          <div style={{ fontSize: '11px', color: '#1B2B5E', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '4px' }}>{isLtfi ? 'Tax-Free Income For Life' : (adv >= 0 ? 'Your Family Keeps' : 'Your Family Gives Up')}</div>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: '#1B2B5E' }}>{adv >= 0 ? '▲ +' : '▼ −'}{fmtDM(Math.abs(adv))}{isLtfi ? '/yr' : ''}</div>
                          <div style={{ fontSize: '10px', color: '#1B2B5E', marginTop: '3px' }}>{isLtfi ? 'every year for life' : `${adv >= 0 ? 'more' : 'less'} at age ${finalAge}`}</div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}
            <div className="pp-narrative-box" style={{ marginTop: '16px' }}>
              {isLtfi ? (
                <>
                  {storyHasTfli ? (() => {
                    const endConvRoth6 = storyExtras?.conversionCompleteRow?.rothBalance
                      ?? ([...eRows].reverse().find(r => num(r.conversionAmount) > 0)?.rothBalance ?? 0);
                    const growthPct = parseFloat(form.growthRate) || 7;
                    return (
                      <>
                        <p className="pp-narrative-p">By age {finalAge}, your plan is projected to have paid you <strong>{fmtDM(storyTfliPaid)}</strong> of tax-free income along the way — <strong>{fmtD(storyTfliYear1)}</strong> this year, then <strong>{fmtD(storyTfliSteady)}/yr</strong> while your IRA converts. At {storyDebtPaidAge ?? 'the end of conversions'} your IRA is $0 and your Roth is <strong>{fmtDM(endConvRoth6)}</strong> — all of it tax-free.</p>
                        <p className="pp-narrative-p">From there, it's your dial, year by year: hold the <strong>{fmtD(storyTfliSteady)}†</strong> paycheck and let the rest compound — at just the plan's {growthPct}% assumption, that leaves <strong>{fmtDM(eFinalRow?.rothBalance || 0)}</strong> of tax-free Roth for your family at {finalAge} — or turn the paycheck up to as much as <strong>{fmtDM(ltfiIncome)}/yr†</strong>. Income or legacy, in any mix — your choice, and the IRS is out of the decision: they collect <strong>{fmtDM(Math.abs(taxSaved))}</strong> less, and forced RMDs of <strong>{fmtDM(rmdElim)}</strong> never happen.</p>
                      </>
                    );
                  })() : (
                    <>
                      <p className="pp-narrative-p">Your plan generates <strong>{fmtDM(ltfiIncome)}/yr</strong> of tax-free income for life, saves <strong>{fmtDM(Math.abs(taxSaved))}</strong> in lifetime taxes, and eliminates <strong>{fmtDM(rmdElim)}</strong> in forced RMDs — so you decide when and how you use your money, not the IRS.</p>
                      <p className="pp-narrative-p">And if tax rates rise, the advantage only grows.</p>
                    </>
                  )}
                  <p className="pp-narrative-p">The engine that made it happen paid <strong>{fmtDM(jbfFunded)}</strong> of conversion tax from inside your account — money you never had to write a check for.</p>
                </>
              ) : (
                <>
                  {legacyGain >= 0 ? (
                    <p className="pp-narrative-p">The direction is clear: your legacy improves by <strong>{fmtDM(legacyGain)}</strong>, and you save <strong>{fmtDM(Math.abs(taxSaved))}</strong> in lifetime taxes.</p>
                  ) : (
                    <p className="pp-narrative-p">You save <strong>{fmtDM(Math.abs(taxSaved))}</strong> in lifetime taxes and eliminate <strong>{fmtDM(rmdElim)}</strong> in forced RMDs; your modeled age-{finalAge} legacy ends <strong>{fmtDM(Math.abs(legacyGain))}</strong> lower than doing nothing — this schedule's conversion tax exceeds the debt it retires.</p>
                  )}
                  <p className="pp-narrative-p">And if tax rates rise, you save even more.</p>
                  <p className="pp-narrative-p">The engine that made it happen paid <strong>{fmtDM(jbfFunded)}</strong> of conversion tax from inside your account — money you never had to write a check for.</p>
                </>
              )}
            </div>
            {ltfiEdition && <LifeFootnote />}
            <div className="jbf-disclosure" style={{ marginTop: '12px', borderTop: '1px solid #e0e0e0', paddingTop: '8px', color: '#666' }}>
              All projections, illustrations, and scenario comparisons are for educational and illustrative purposes only. No offer or sale of any security is expressed or implied. Past performance does not guarantee future results. Consult your financial, tax, and legal advisors before making investment decisions.
            </div>
          </Page>
        );
      })()}

      {/* PAGE 7 — WHY JESSE */}
      <Page num={whyJessePageNum} total={totalPages} clientName={fullNames}>
        <SectionTitle>Why This Works — And Why Jesse Bradin Is The Right Advisor To Build It</SectionTitle>
        <div className="pp-why-blocks">
          <div className="pp-why-block">
            <div className="pp-why-block-header">{ltfiEdition ? 'Most Advisors Manage Money. Jesse Builds Paychecks the IRS Can’t Touch.' : 'Most Advisors Manage Money. Few Eliminate the Tax.'}</div>
            <p className="pp-narrative-p">The IRA sitting in your retirement account is not fully yours.</p>
            <p className="pp-narrative-p">Every dollar is pre-taxed — the IRS has a claim on it.</p>
            <p className="pp-narrative-p">Most advisors accept that as a given.</p>
            <p className="pp-narrative-p">{ltfiEdition ? 'Jesse Bradin built a system custom for you to remove that claim — and turn what remains into tax-free income you can actually live on.' : 'Jesse Bradin built a system custom for you to remove that claim entirely.'}</p>
          </div>
          <div className="pp-why-block">
            <div className="pp-why-block-header">Why Not the "Textbook" Conversion?</div>
            <p className="pp-narrative-p">On paper, there are more tax-efficient ways to convert — writing the tax checks from outside savings, hand-optimizing every bracket, every year.</p>
            <p className="pp-narrative-p">In practice, almost nobody does it. Five- and six-figure checks to the IRS, year after year, are why most Roth conversions stay a theoretical aspiration while the debt keeps growing.</p>
            <p className="pp-narrative-p">Jesse builds the conversion people actually complete: a small Roth engine pays the tax from inside your account, you decide once, and the plan runs without you.</p>
            <p className="pp-narrative-p">The JBF Convert Engine is a proprietary process developed exclusively by J Bradin Financial — sized specifically to your accounts and timeline, and managed on a discretionary basis by Jesse Bradin for <strong>{(form.includeSpouse && form.spouseFirstName) ? `${form.clientFirstName} and ${form.spouseFirstName}` : form.clientFirstName || clientName}</strong>.</p>
          </div>
          <div className="pp-why-block">
            <div className="pp-why-block-header">The Plan You Are Holding Was Built For You.</div>
            <p className="pp-narrative-p">Every number in this document reflects your accounts, your income, your timeline, and your goals.</p>
            <p className="pp-narrative-p">This is not a template.</p>
            <p className="pp-narrative-p">This is your conversion roadmap — built line by line from your numbers, and updated as your situation changes.</p>
          </div>
        </div>
        <div className="pp-why-closing">"If you want this built for you, this is where it gets built."</div>
      </Page>

      {/* PAGE 8 — DISCLOSURES */}
      <Page num={disclosuresPageNum} total={totalPages}>
        <SectionTitle>Important Disclosures</SectionTitle>
        <div className="pp-disclosure-body">
          <p>The projections contained in this report are based on client-provided inputs and reasonable assumptions believed to be accurate at the time of preparation. All values shown are hypothetical illustrations only and are intended for educational and planning purposes.</p>
          <p>No guarantee of future results is expressed or implied. Investment returns, tax rates, inflation, Social Security benefits, and other variables may differ materially from the assumptions used in this report. Actual outcomes will vary.</p>
          <p>This report does not constitute tax, legal, or investment advice. The scenarios presented are illustrative comparisons only and should not be construed as a recommendation to purchase any specific financial product or pursue any specific strategy.</p>
          <p>Roth conversion strategies involve complex tax considerations. The tax impact of any conversion depends on your total income, deductions, filing status, state tax laws, and future legislation, all of which are subject to change. Consult a qualified tax advisor or attorney before making any financial, tax, or investment decisions.</p>
          <p>Social Security projections are estimates based on current benefit rules, which may change. RMD calculations are based on current IRS life expectancy tables and applicable law, which are also subject to change.</p>
          <p>References to your IRA's "tax debt" or the IRS's "claim" describe the modeled income tax projected to be due on pre-tax retirement balances when withdrawn or inherited (illustrated here as a 10-year inherited-IRA payout at your heirs' modeled rates). This is an illustrative planning estimate — not a tax bill, lien, assessment, or amount currently owed.</p>
          <p className="pp-disclosure-section-title">JBF Convert Engine — Important Notes</p>
          {form.rothScenarios.some(s => CE_TIERS[s.tier]) ? (
            <p>The JBF Convert Engine scenario uses a tiered planning distribution rate (8%, 9%, or 10% depending on the risk-matched portfolio tier) applied to a portfolio of defined-income structured notes and covered-call income ETFs. The planning rate is a distribution assumption — not a total-return assumption — and is deliberately set below current indicative note coupons. Engine principal is modeled flat (no growth assumed on the engine sleeve). Structured notes are unsecured obligations of the issuing bank, coupons are contingent on index barriers, principal protection applies only at the stated barrier at maturity, and issuer call features create reinvestment risk. Rates are for plan design purposes only — they are not guaranteed and may not be achieved.</p>
          ) : (
            <p>The JBF Convert Engine scenario uses a 14% gross yield assumption (11% distributed, 3% retained for growth) based on the current JBF Income Portfolio objective. This yield assumption is for plan design purposes only — it is not guaranteed and may not be achieved. Portfolio distributions and total return will fluctuate based on market conditions, interest rates, dividend policy changes, and portfolio composition. Past distribution rates are not indicative of future results.</p>
          )}
          {storyHasTfli && (
            <p>TFLI ("Tax-Free Lifetime Income") sleeve distributions use a planning rate (6%–10%) applied to a Roth income portfolio; the rate is a distribution assumption — not a guaranteed yield or total-return projection — and sleeve principal is modeled flat. "For life" figures marked with † are projections to the plan's stated life expectancy under these assumptions; actual distributions are not guaranteed and will vary with portfolio performance and tax law.</p>
          )}
          <p>The tax-funded conversion model assumes JBF Income Portfolio distributions are sufficient to cover Roth conversion tax costs during the conversion period. Actual distributions may be higher or lower than projected. If distributions fall short, additional outside funds would be required to cover conversion taxes, which could reduce the projected legacy benefit.</p>
          <p>The JBF Income Portfolio is a managed portfolio of monthly dividend ETFs designed to target high-yield distributions. Portfolio values fluctuate and past performance is not indicative of future results. Any stated yield, distribution rate, or return is a portfolio objective only — not a guarantee or promise of performance. JBF manages this portfolio on a discretionary basis.</p>
          <p>Roth-Funded Option: When conversion taxes are funded from an existing Roth IRA balance, distributions reduce the tax-free Roth principal available for retirement income. The net benefit depends on conversion tax savings versus reduction in Roth balance. This illustration assumes Roth distributions are tax-free qualified distributions. Consult a tax advisor to confirm eligibility.</p>
          <p className="pp-disclosure-firm">J Bradin Financial — Prepared by Jesse Bradin. This report is confidential and intended solely for the use of the individual named herein.</p>
        </div>
      </Page>

    </div>
  );
}
